import clsx from "clsx";
import { Button } from "@repo/ui/button";
import { Container } from "@repo/ui/flex";
import * as Sheet from "@repo/ui/sheet";
import { VisuallyHidden } from "@repo/ui/visually-hidden";
import classes from "./product-variant-sheet.module.css";

export type MappedVariant = {
  value: string;
  label: string;
  status: "default" | "disabled" | "alert";
  statusLabel?: string;
};

export type ProductVariantSheetProps = {
  mappedVariants: MappedVariant[];
  onSelect: (value: string) => void;
  placeholder: string;
  icon?: React.ReactNode;
  closeIcon: React.ReactNode;
  itemIndicatorContent?: string | React.ReactNode;
  selectedValue?: string | null;
  selectItemClassName?: string;
  selectHeaderClassName?: string;
  selectTriggerClassName?: string;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  className?: string;
  iconClassName?: string;
  header?: string;
  sheetType?: "action" | "select";
  variant?: "primary" | "secondary";
};

export const ProductVariantSheet = ({
  mappedVariants,
  onSelect,
  placeholder,
  icon,
  closeIcon,
  itemIndicatorContent,
  selectedValue,
  selectItemClassName,
  selectHeaderClassName,
  selectTriggerClassName,
  isOpen,
  onOpenChange,
  disabled,
  className,
  iconClassName,
  header,
  variant = "secondary",
  sheetType = "select",
}: ProductVariantSheetProps) => (
  <Sheet.Root open={isOpen} onOpenChange={onOpenChange}>
    <Sheet.Trigger asChild>
      <Button
        variant={variant}
        size="large"
        alignment="spaceBetween"
        fullWidth
        className={clsx(
          disabled && classes.disabledTrigger,
          classes.trigger,
          className,
          selectTriggerClassName,
        )}
        disabled={disabled}
        data-testid="select-trigger"
      >
        {sheetType === "action" ? (
          <>{placeholder}</>
        ) : (
          <>
            {mappedVariants.find((v) => v.value === selectedValue)?.label ??
              placeholder}
            {icon && (
              <Container
                justify="center"
                align="center"
                className={clsx(classes.iconContainer, iconClassName)}
              >
                {icon}
              </Container>
            )}
          </>
        )}
      </Button>
    </Sheet.Trigger>

    <Sheet.Overlay className={classes.overlay} overlayAnimation />

    <Sheet.Title>
      <VisuallyHidden>{placeholder}</VisuallyHidden>
    </Sheet.Title>

    <Sheet.Content
      className={classes.content}
      position="bottom"
      overlayAnimation
    >
      <Sheet.Close asChild>
        <button className={classes.closeButton}>{closeIcon}</button>
      </Sheet.Close>

      <div className={classes.scrollWrapper}>
        <Sheet.Header
          className={clsx(classes.selectheader, selectHeaderClassName)}
        >
          {header ? header : placeholder}
        </Sheet.Header>

        <ul className={classes.variantList}>
          {mappedVariants.map(({ value, label, status, statusLabel }) => {
            const isSelected = selectedValue === value;
            const button = (
              <button
                className={clsx(
                  selectItemClassName,
                  classes.itemText,
                  status === "disabled" && classes.disabledLabel,
                )}
                onClick={() => onSelect(value)}
                disabled={status === "disabled"}
              >
                <span className={classes.buttonInner}>
                  {isSelected && itemIndicatorContent && (
                    <span className={classes.itemIndicator}>
                      {itemIndicatorContent}
                    </span>
                  )}
                  <span>{label}</span>
                </span>
                {statusLabel && (
                  <span
                    className={clsx(
                      classes.statusLabel,
                      status === "alert" && classes.alertLabel,
                    )}
                  >
                    {statusLabel}
                  </span>
                )}
              </button>
            );

            return (
              <li key={value}>
                <Container
                  asChild
                  align="center"
                  justify="start"
                  className={classes.item}
                >
                  {status === "disabled" ? (
                    button
                  ) : (
                    <Sheet.Close asChild>{button}</Sheet.Close>
                  )}
                </Container>
              </li>
            );
          })}
        </ul>
      </div>
    </Sheet.Content>
  </Sheet.Root>
);
